import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Thermometer, 
  Fan, 
  Wrench, 
  Shield, 
  Phone, 
  MapPin, 
  Clock, 
  CheckCircle,
  AlertTriangle,
  Mail,
  ArrowRight
} from 'lucide-react';

const HVACDemo = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const services = [
    {
      icon: Fan,
      title: 'AC Repair',
      description: 'Fast diagnosis and repair for all AC brands. Same-day service available.',
      price: 'Starting at $89'
    },
    {
      icon: Thermometer,
      title: 'Heating Repair',
      description: 'Expert furnace and heat pump repairs to keep you warm all winter.',
      price: 'Starting at $89'
    },
    {
      icon: Wrench,
      title: 'HVAC Installation',
      description: 'Professional installation of new heating and cooling systems.',
      price: 'Free Estimates'
    },
    {
      icon: Shield,
      title: 'Maintenance Plans',
      description: 'Preventive maintenance to extend system life and reduce bills.',
      price: 'From $149/year'
    }
  ];

  const testimonials = [
    {
      name: "Robert M.",
      text: "Called them on a Sunday when our AC died. They came out within 2 hours and had us cool again. Excellent service!",
      rating: 5
    },
    {
      name: "Jennifer K.",
      text: "Very professional team. They explained everything clearly and didn't try to upsell unnecessary repairs. Fair pricing too.",
      rating: 5
    },
    {
      name: "David L.",
      text: "Been using them for our annual maintenance for 3 years. Always on time, thorough, and reasonably priced.",
      rating: 5
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sticky Mobile Call Button */}
      <a
        href="tel:7705552020"
        className="fixed bottom-4 right-4 z-50 md:hidden bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 font-semibold"
      >
        <Phone size={20} />
        Call Now
      </a>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-blue-900 flex items-center justify-center">
              <Thermometer className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Peak HVAC</h1>
              <p className="text-xs text-slate-500 hidden sm:block">Services</p>
            </div>
          </div>
          
          <div className="hidden md:flex items-center gap-6">
            <a href="#services" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Services</a>
            <a href="#testimonials" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Reviews</a>
            <a href="#contact" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Contact</a>
          </div>

          <div className="flex items-center gap-3">
            <a 
              href="tel:7705552020"
              className="hidden md:flex items-center gap-2 text-blue-900 font-semibold hover:text-blue-700"
            >
              <Phone size={18} />
              (770) 555-2020
            </a>
            <Button
              data-testid="nav-request-service-btn"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Request Service
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 sm:pt-32 sm:pb-24 px-4 sm:px-6 bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-full text-orange-300 text-sm mb-6">
                <AlertTriangle size={16} />
                24/7 Emergency Service Available
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Fast, Reliable HVAC Service You Can Trust
              </h1>
              <p className="text-lg sm:text-xl text-blue-100 mb-8 max-w-xl">
                Expert heating and cooling repairs, installations, and maintenance. Serving Dallas, GA and surrounding areas.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="hero-request-service-btn"
                  size="lg"
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-orange-500 hover:bg-orange-600 text-white text-lg px-8 py-6"
                >
                  Request Service
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <a
                  href="tel:7705552020"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 border-2 border-white/30 rounded-lg text-white hover:bg-white/10 transition-colors text-lg font-medium"
                >
                  <Phone size={20} />
                  (770) 555-2020
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <img
                src="https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=800&q=80"
                alt="HVAC technician at work"
                className="rounded-xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 px-4 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { label: 'Same-Day Service', value: 'Available' },
              { label: 'Years Experience', value: '15+' },
              { label: 'Customer Rating', value: '4.9★' },
              { label: 'Jobs Completed', value: '5,000+' }
            ].map((stat, idx) => (
              <div key={idx} className="py-4">
                <div className="text-2xl font-bold text-blue-900">{stat.value}</div>
                <div className="text-sm text-slate-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive HVAC solutions for your home or business
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, idx) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white p-8 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all"
              >
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-5">
                  <service.icon className="w-7 h-7 text-blue-900" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600 mb-4">{service.description}</p>
                <p className="text-orange-500 font-semibold">{service.price}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency CTA */}
      <section className="py-16 px-4 sm:px-6 bg-orange-500">
        <div className="max-w-4xl mx-auto text-center text-white">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            HVAC Emergency?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            We offer 24/7 emergency service. Don't suffer through extreme temperatures!
          </p>
          <a
            href="tel:7705552020"
            className="inline-flex items-center gap-3 bg-white text-orange-500 hover:bg-orange-50 px-8 py-4 rounded-lg text-lg font-bold transition-colors"
          >
            <Phone size={24} />
            Call Now: (770) 555-2020
          </a>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 bg-slate-100">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Don't just take our word for it
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white p-8 rounded-xl border border-slate-200"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-500 text-lg">★</span>
                  ))}
                </div>
                <p className="text-slate-600 mb-4 italic">"{testimonial.text}"</p>
                <p className="font-semibold text-slate-900">— {testimonial.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-20 px-4 sm:px-6 bg-blue-900">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-white"
            >
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Request Service
              </h2>
              <p className="text-blue-200 mb-8">
                Fill out the form and we'll get back to you within 1 hour during business hours.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Phone</p>
                    <a href="tel:7705552020" className="text-blue-200 hover:text-white">(770) 555-2020</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Email</p>
                    <a href="mailto:service@peakhvacservices.com" className="text-blue-200 hover:text-white">service@peakhvacservices.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Service Area</p>
                    <p className="text-blue-200">123 Main Street<br/>Dallas, GA 30132</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Hours</p>
                    <p className="text-blue-200">Mon-Fri: 7am - 7pm<br/>Sat: 8am - 5pm<br/>Emergency: 24/7</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-xl p-8">
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                      <Input
                        data-testid="contact-name-input"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Phone *</label>
                      <Input
                        data-testid="contact-phone-input"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                      <Input
                        data-testid="contact-email-input"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Service Needed *</label>
                      <select
                        data-testid="contact-service-select"
                        value={formData.service}
                        onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Select a service</option>
                        <option value="ac-repair">AC Repair</option>
                        <option value="heating-repair">Heating Repair</option>
                        <option value="installation">New Installation</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="emergency">Emergency Service</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Describe the Issue</label>
                      <Textarea
                        data-testid="contact-message-input"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us about your HVAC issue..."
                        className="min-h-[100px]"
                      />
                    </div>
                    <Button
                      data-testid="contact-submit-btn"
                      type="submit"
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg"
                    >
                      Request Service
                    </Button>
                  </form>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Request Received!</h3>
                    <p className="text-slate-600">We'll contact you within 1 hour during business hours.</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-800 flex items-center justify-center">
                  <Thermometer className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-bold">Peak HVAC Services</h4>
                  <p className="text-xs text-slate-400">Heating & Cooling Experts</p>
                </div>
              </div>
              <p className="text-slate-400 text-sm">
                Fast, reliable HVAC service you can trust. Serving Dallas, GA and surrounding areas.
              </p>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Contact</h5>
              <div className="space-y-2 text-sm text-slate-400">
                <p><a href="tel:7705552020" className="hover:text-white">(770) 555-2020</a></p>
                <p><a href="mailto:service@peakhvacservices.com" className="hover:text-white">service@peakhvacservices.com</a></p>
                <p>123 Main Street<br/>Dallas, GA 30132</p>
              </div>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Services</h5>
              <div className="space-y-2 text-sm text-slate-400">
                <p>AC Repair</p>
                <p>Heating Repair</p>
                <p>HVAC Installation</p>
                <p>Maintenance Plans</p>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
            <p>© 2026 Peak HVAC Services. All rights reserved.</p>
            <p className="mt-2 text-xs italic">Demo website template for HVAC contractors</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HVACDemo;
